import { useState, useEffect } from 'react'
import { useNavigate, Link, useSearchParams } from 'react-router-dom'
import {
  Stethoscope,
  Eye,
  EyeOff,
  Sparkles,
  Headphones,
  ShieldCheck,
  BarChart3,
  Shield,
  Mail,
  Lock,
  ArrowRight,
  Users,
  CalendarDays,
  Star,
  Cloud,
  HeadphonesIcon,
  type LucideIcon,
} from 'lucide-react'
import { useAuthStore } from '../stores/authStore'
import api from '../lib/api'
import toast from 'react-hot-toast'

type DemoUser = {
  id: string
  role: string
  name: string
  email: string
  password: string
  description: string
  icon: LucideIcon
  accent: string
}

const IS_DEMO = import.meta.env.MODE !== 'production'

const DEMO_USERS: DemoUser[] = [
  {
    id: 'dentist',
    role: 'Odontologo',
    name: 'Dr. Carlos Lopez',
    email: 'dr.lopez@smartdental.es',
    password: 'smartdental2024',
    description: 'Odontograma, tratamientos y evoluciones',
    icon: Stethoscope,
    accent: 'bg-primary-50 text-primary-600',
  },
  {
    id: 'hygienist',
    role: 'Higienista',
    name: 'Laura Garcia',
    email: 'higienista@smartdental.es',
    password: 'smartdental2024',
    description: 'Higiene, prevencion y asistencia clinica',
    icon: Sparkles,
    accent: 'bg-emerald-50 text-emerald-600',
  },
  {
    id: 'receptionist',
    role: 'Recepcionista',
    name: 'Maria Sanchez',
    email: 'recepcion@smartdental.es',
    password: 'smartdental2024',
    description: 'Agenda, pacientes y cobros en mostrador',
    icon: Headphones,
    accent: 'bg-amber-50 text-amber-600',
  },
  {
    id: 'admin',
    role: 'Administrador',
    name: 'Pedro Ruiz',
    email: 'admin@smartdental.es',
    password: 'smartdental2024',
    description: 'Facturacion, usuarios y configuracion',
    icon: ShieldCheck,
    accent: 'bg-indigo-50 text-indigo-600',
  },
  {
    id: 'manager',
    role: 'Gerente',
    name: 'Sofia Vargas',
    email: 'gerente@smartdental.es',
    password: 'smartdental2024',
    description: 'KPIs, analitica y decisiones estrategicas',
    icon: BarChart3,
    accent: 'bg-rose-50 text-rose-600',
  },
]

export function LoginPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { setAuth, setReadOnly } = useAuthStore()

  // Determinar usuario inicial desde query param ?role=...
  const roleFromUrl = searchParams.get('role')
  const initialUser = DEMO_USERS.find((u) => u.id === roleFromUrl) || DEMO_USERS[0]

  const [selectedId, setSelectedId] = useState<string>(initialUser.id)
  const [email, setEmail] = useState(initialUser.email)
  const [password, setPassword] = useState(initialUser.password)
  const [showPassword, setShowPassword] = useState(false)
  const [rememberMe, setRememberMe] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  // Limpiar flag residual de modo demo de sesiones anteriores
  useEffect(() => {
    sessionStorage.removeItem('SmartDental_readonly')
  }, [])

  // Autologin en modo demo read-only desde query params
  useEffect(() => {
    const demoMode = searchParams.get('demo')
    const roleParam = searchParams.get('role')

    if (demoMode === 'readonly' && roleParam) {
      const demoUser = DEMO_USERS.find((u) => u.id === roleParam)
      if (demoUser) {
        setIsLoading(true)
        api
          .post('/auth/demo-login', { role: roleParam })
          .then(({ data }) => {
            if (data.access_token) {
              sessionStorage.setItem('SmartDental_token', data.access_token)
            }
            setAuth(data.user)
            // Si el backend marca al usuario como demo, activar read-only
            if (data.user?.isDemo) {
              setReadOnly(true)
              toast.success(`Bienvenido a la demo de SmartDental. Modo solo lectura.`)
            } else {
              toast.success(`Bienvenido, ${data.user.firstName} ${data.user.lastName}`)
            }
            // Limpiar query params de la URL
            navigate('/', { replace: true })
          })
          .catch((err: any) => {
            toast.error(err.response?.data?.message || 'Error al iniciar sesion de demo')
            setIsLoading(false)
          })
      }
    }
  }, [searchParams, setAuth, setReadOnly, navigate])

  const handleSelectDemo = (user: DemoUser) => {
    setSelectedId(user.id)
    setEmail(user.email)
    setPassword(user.password)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      const { data } = await api.post('/auth/login', { email, password })
      if (data.access_token) {
        sessionStorage.setItem('SmartDental_token', data.access_token)
      }
      setAuth(data.user)
      toast.success(`Bienvenido, ${data.user.firstName} ${data.user.lastName}`)
      navigate('/')
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Error al iniciar sesion')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen relative bg-slate-50">
      {/* Fondo: imagen con desvanecimiento por la derecha via mask-image */}
      <div
        className="absolute inset-0"
        style={{
          maskImage: 'linear-gradient(to right, black 55%, transparent 85%)',
          WebkitMaskImage: 'linear-gradient(to right, black 55%, transparent 85%)',
        }}
      >
        <img
          src="/imagenapoyologin.png"
          alt=""
          className="w-full h-full object-cover"
        />
      </div>

      {/* Overlay oscuro izquierda para legibilidad del texto */}
      <div className="absolute inset-0 bg-gradient-to-r from-teal-950/85 via-teal-900/35 to-transparent pointer-events-none" />

      {/* Contenido escritorio */}
      <div className="hidden md:flex relative z-10 min-h-screen">
        {/* Panel izquierdo: contenido promocional */}
        <div className="w-1/2 flex flex-col p-10 lg:p-12 text-white overflow-y-auto">
          {/* Logo */}
          <div className="flex items-center gap-2.5 shrink-0 mb-6">
            <img src="/logo.png" alt="" className="w-7 h-7" />
            <span className="text-lg font-semibold tracking-tight">SmartDental</span>
          </div>

          {/* Contenido */}
          <div className="space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-sm border border-white/10 text-xs font-medium">
              <Shield className="w-3.5 h-3.5" />
              ERP DENTAL INTEGRAL
            </div>

            <div>
              <h2 className="text-3xl lg:text-4xl font-bold leading-[1.1]">
                La plataforma dental
                <br />
                <span className="text-teal-400">todo en uno</span>
              </h2>
              <p className="mt-3 text-white/60 text-sm max-w-sm leading-relaxed">
                Gestiona pacientes, citas, historiales, facturacion y mucho mas desde una unica plataforma.
              </p>
            </div>

            {/* Divider */}
            <div className="w-12 h-px bg-white/20" />

            {/* Estadisticas compactas */}
            <div className="flex gap-6">
              <div className="flex items-center gap-2.5">
                <Users className="w-4 h-4 text-white/60" />
                <div>
                  <p className="text-sm font-bold">+120</p>
                  <p className="text-[10px] text-white/40">Clinicas activas</p>
                </div>
              </div>
              <div className="flex items-center gap-2.5">
                <CalendarDays className="w-4 h-4 text-white/60" />
                <div>
                  <p className="text-sm font-bold">+45.000</p>
                  <p className="text-[10px] text-white/40">Citas al mes</p>
                </div>
              </div>
              <div className="flex items-center gap-2.5">
                <ShieldCheck className="w-4 h-4 text-white/60" />
                <div>
                  <p className="text-sm font-bold">99,9%</p>
                  <p className="text-[10px] text-white/40">Uptime</p>
                </div>
              </div>
            </div>

            {/* Tarjeta seguridad */}
            <div className="bg-white/10 backdrop-blur-md border border-white/10 rounded-xl p-4 max-w-xs">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <Lock className="w-4 h-4 text-teal-400" />
                  <span className="text-sm font-semibold">Tus datos, siempre seguros</span>
                </div>
                <div className="flex flex-col items-center">
                  <div className="flex gap-0.5">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star key={i} className="w-3 h-3 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <span className="text-[9px] text-white/40 mt-0.5">RGPD</span>
                </div>
              </div>
              <p className="mt-2 text-[11px] text-white/40 leading-relaxed">
                Cumplimos con el RGPD y las mas altas normas de seguridad en la nube.
              </p>
            </div>

            {/* Testimonial */}
            <div className="bg-white/5 border border-white/10 rounded-xl p-4 max-w-xs">
              <div className="flex gap-1 mb-2">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} className="w-3.5 h-3.5 fill-teal-400 text-teal-400" />
                ))}
              </div>
              <p className="text-sm text-white/70 leading-relaxed">
                "SmartDental ha transformado la forma en que gestionamos nuestra clinica."
              </p>
              <p className="mt-2 text-xs text-white/30">Clinica Dental Sonrie</p>
            </div>
          </div>
        </div>

        {/* Panel derecho: tarjeta flotante */}
        <div className="flex-1 flex items-center justify-center p-6 lg:p-10">
          <div className="w-full max-w-[400px]">
            {/* Tarjeta blanca */}
            <div className="bg-white rounded-3xl shadow-[0_8px_40px_-12px_rgba(0,0,0,0.12)] p-8 lg:p-10">
              {/* Logo */}
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-primary-50 mb-3">
                  <img src="/logo.png" alt="SmartDental" className="w-9 h-9" />
                </div>
                <h1 className="text-xl font-bold text-text-primary">SmartDental</h1>
                <p className="text-sm text-text-tertiary mt-0.5">ERP Dental Integral</p>
                <p className="text-base font-semibold text-text-primary mt-4">Bienvenido de nuevo 👋</p>
                <p className="text-sm text-text-tertiary mt-0.5">Inicia sesion para continuar</p>
              </div>

              {/* Formulario */}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-text-primary mb-1.5">
                    Correo electronico
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-text-tertiary" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full pl-10 pr-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-text-primary placeholder-text-tertiary outline-none focus:border-primary-600 focus:ring-2 focus:ring-primary-100 transition-all min-h-[44px]"
                      placeholder="doctor@clinica.es"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-text-primary mb-1.5">
                    Contrasena
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-text-tertiary" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full pl-10 pr-10 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-text-primary placeholder-text-tertiary outline-none focus:border-primary-600 focus:ring-2 focus:ring-primary-100 transition-all min-h-[44px]"
                      placeholder="••••••••"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary hover:text-text-secondary"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 text-sm text-text-secondary cursor-pointer">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      className="w-4 h-4 rounded border-border text-primary-600 focus:ring-primary-300"
                    />
                    Recordarme
                  </label>
                  <Link to="/forgot-password" className="text-sm text-primary-600 hover:text-primary-700 transition-colors">
                    Olvide mi contraseña
                  </Link>
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-gradient-to-r from-primary-500 to-primary-700 hover:from-primary-600 hover:to-primary-800 disabled:opacity-50 text-white text-sm font-semibold px-4 py-3 rounded-xl transition-all min-h-[48px] flex items-center justify-center gap-2 shadow-lg shadow-primary-500/25"
                >
                  {isLoading ? (
                    <>
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Iniciando sesion...
                    </>
                  ) : (
                    <>
                      Acceder al panel
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </form>

              {/* Footer confianza */}
              <div className="mt-6 flex items-center justify-center gap-5 text-[11px] text-text-tertiary">
                <div className="flex items-center gap-1.5">
                  <Lock className="w-3 h-3" />
                  Cifrado SSL
                </div>
                <div className="flex items-center gap-1.5">
                  <Cloud className="w-3 h-3" />
                  Backups diarios
                </div>
                <div className="flex items-center gap-1.5">
                  <HeadphonesIcon className="w-3 h-3" />
                  Soporte 24/7
                </div>
              </div>
            </div>

            {/* Demo users - solo dev */}
            {IS_DEMO && (
              <div className="mt-4 bg-white rounded-2xl shadow-sm border border-slate-100 p-5">
                <div className="mb-3">
                  <h2 className="text-sm font-semibold text-text-primary">Usuarios de demostracion</h2>
                  <p className="text-xs text-text-tertiary mt-0.5">Selecciona un perfil para autocompletar.</p>
                </div>
                <div className="space-y-2">
                  {DEMO_USERS.map((user) => {
                    const Icon = user.icon
                    const isSelected = selectedId === user.id
                    return (
                      <button
                        key={user.id}
                        type="button"
                        onClick={() => handleSelectDemo(user)}
                        className={`w-full text-left flex items-center gap-3 p-2.5 rounded-lg border transition-all ${
                          isSelected
                            ? 'border-primary-600 ring-2 ring-primary-100 bg-primary-50/40'
                            : 'border-slate-100 hover:border-primary-200 hover:bg-slate-50'
                        }`}
                      >
                        <div className={`shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${user.accent}`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-text-primary truncate">{user.role}</span>
                            <span className="text-[11px] text-text-tertiary truncate">{user.name}</span>
                          </div>
                        </div>
                      </button>
                    )
                  })}
                </div>
                <p className="mt-3 text-[11px] text-text-tertiary">
                  Contrasena: <span className="font-mono">smartdental2024</span>
                </p>
              </div>
            )}

            {/* Links legales */}
            <div className="mt-4 flex items-center justify-center gap-3 text-xs text-text-tertiary">
              <Link to="/privacidad" target="_blank" className="hover:text-primary-600 transition-colors">Politica de privacidad</Link>
              <span>·</span>
              <Link to="/aviso-legal" target="_blank" className="hover:text-primary-600 transition-colors">Aviso legal</Link>
              <span>·</span>
              <Link to="/cookies" target="_blank" className="hover:text-primary-600 transition-colors">Cookies</Link>
            </div>
          </div>
        </div>
      </div>

      {/* Movil */}
      <div className="md:hidden relative z-10 min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-[400px]">
          {/* Tarjeta blanca */}
          <div className="bg-white/95 backdrop-blur-sm rounded-3xl shadow-xl p-6">
            {/* Logo */}
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-primary-50 mb-3">
                <img src="/logo.png" alt="SmartDental" className="w-9 h-9" />
              </div>
              <h1 className="text-xl font-bold text-text-primary">SmartDental</h1>
              <p className="text-sm text-text-tertiary mt-0.5">ERP Dental Integral</p>
              <p className="text-base font-semibold text-text-primary mt-3">Bienvenido de nuevo 👋</p>
              <p className="text-sm text-text-tertiary mt-0.5">Inicia sesion para continuar</p>
            </div>

            {/* Formulario */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-text-primary mb-1.5">Correo electronico</label>
                <div className="relative">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-text-tertiary" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-10 pr-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-text-primary placeholder-text-tertiary outline-none focus:border-primary-600 focus:ring-2 focus:ring-primary-100 transition-all min-h-[44px]"
                    placeholder="doctor@clinica.es"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-text-primary mb-1.5">Contrasena</label>
                <div className="relative">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-text-tertiary" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-10 pr-10 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-text-primary placeholder-text-tertiary outline-none focus:border-primary-600 focus:ring-2 focus:ring-primary-100 transition-all min-h-[44px]"
                    placeholder="••••••••"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary hover:text-text-secondary"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-sm text-text-secondary cursor-pointer">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="w-4 h-4 rounded border-border text-primary-600 focus:ring-primary-300"
                  />
                  Recordarme
                </label>
                <Link to="/forgot-password" className="text-sm text-primary-600 hover:text-primary-700 transition-colors">Olvide mi contraseña</Link>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-gradient-to-r from-primary-500 to-primary-700 hover:from-primary-600 hover:to-primary-800 disabled:opacity-50 text-white text-sm font-semibold px-4 py-3 rounded-xl transition-all min-h-[48px] flex items-center justify-center gap-2 shadow-lg shadow-primary-500/25"
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Iniciando sesion...
                  </>
                ) : (
                  <>Acceder al panel<ArrowRight className="w-4 h-4" /></>
                )}
              </button>
            </form>

            {/* Footer confianza */}
            <div className="mt-5 flex items-center justify-center gap-4 text-[10px] text-text-tertiary">
              <div className="flex items-center gap-1"><Lock className="w-3 h-3" />Cifrado SSL</div>
              <div className="flex items-center gap-1"><Cloud className="w-3 h-3" />Backups diarios</div>
              <div className="flex items-center gap-1"><HeadphonesIcon className="w-3 h-3" />Soporte 24/7</div>
            </div>
          </div>

          {/* Demo users movil */}
          {IS_DEMO && (
            <div className="mt-4 bg-white/95 backdrop-blur-sm rounded-2xl shadow-sm border border-slate-100 p-4">
              <div className="mb-2">
                <h2 className="text-sm font-semibold text-text-primary">Usuarios de demostracion</h2>
                <p className="text-xs text-text-tertiary mt-0.5">Selecciona un perfil para autocompletar.</p>
              </div>
              <div className="space-y-2">
                {DEMO_USERS.map((user) => {
                  const Icon = user.icon
                  const isSelected = selectedId === user.id
                  return (
                    <button
                      key={user.id}
                      type="button"
                      onClick={() => handleSelectDemo(user)}
                      className={`w-full text-left flex items-center gap-3 p-2.5 rounded-lg border transition-all ${
                        isSelected
                          ? 'border-primary-600 ring-2 ring-primary-100 bg-primary-50/40'
                          : 'border-slate-100 hover:border-primary-200 hover:bg-slate-50'
                      }`}
                    >
                      <div className={`shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${user.accent}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-text-primary truncate">{user.role}</span>
                          <span className="text-[11px] text-text-tertiary truncate">{user.name}</span>
                        </div>
                      </div>
                    </button>
                  )
                })}
              </div>
              <p className="mt-2 text-[11px] text-text-tertiary">Contrasena: <span className="font-mono">smartdental2024</span></p>
            </div>
          )}

          {/* Links legales movil */}
          <div className="mt-4 flex items-center justify-center gap-3 text-xs text-text-tertiary">
            <Link to="/privacidad" target="_blank" className="hover:text-primary-600 transition-colors">Politica de privacidad</Link>
            <span>·</span>
            <Link to="/aviso-legal" target="_blank" className="hover:text-primary-600 transition-colors">Aviso legal</Link>
            <span>·</span>
            <Link to="/cookies" target="_blank" className="hover:text-primary-600 transition-colors">Cookies</Link>
          </div>
        </div>
      </div>
    </div>
  )
}
